import React from 'react';
import { Box, Flex, Text, AbstractButton } from 'pounceV2';
import { useTheme } from '@mui/material';
import { useResizeObserver } from 'Hooks/useResizeObserver';

const DEFAULT_LINES = 3;

interface CollapsibleTextV3Props {
  children: React.ReactNode;
  lines?: number;
  expanded: boolean;
  onExpandChange: (expanded: boolean) => void;
  toggleAlignment?: 'flex-start' | 'flex-end';
}

const CollapsibleTextV3: React.FC<CollapsibleTextV3Props> = ({
  children,
  lines = DEFAULT_LINES,
  expanded,
  onExpandChange,
  toggleAlignment = 'flex-start',
}) => {
  const {
    palette: { componentTokens },
  } = useTheme();

  const contentRef = React.useRef<HTMLDivElement>(null);
  const contentId = React.useId();
  const [isTruncatable, setIsTruncatable] = React.useState(false);

  const handleResize = (entry: ResizeObserverEntry): void => {
    const el = entry.target as HTMLElement;
    // Inner element carries the real line-height; the Box wrapper resolves to "normal".
    // Children that wrap a tree (e.g. PromptMarkdownPreviewV3) must set an explicit
    // length-valued line-height on their root so this single-step lookup is deterministic
    // — don't drill deeper, since the first markdown block could be a heading/code block
    // with the wrong baseline. The threshold counts text lines only, so for multi-block
    // markdown the scrollHeight includes inter-block margins that -webkit-line-clamp
    // ignores; a tight cluster of short paragraphs can show "Show more" with no hidden
    // text. Acceptable for realistic AI-generated prompts, which overflow legitimately.
    const typographyEl = el.firstElementChild instanceof HTMLElement ? el.firstElementChild : el;
    const lineHeight = parseFloat(getComputedStyle(typographyEl).lineHeight);
    const threshold =
      Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight * lines : el.clientHeight;
    setIsTruncatable(el.scrollHeight > threshold);
  };

  useResizeObserver(contentRef, handleResize);

  return (
    <Flex direction="column" gap={1} alignItems="stretch">
      <Box
        ref={contentRef}
        id={contentId}
        role="region"
        whiteSpace="pre-wrap"
        sx={{
          ...(!expanded && {
            display: '-webkit-box',
            WebkitLineClamp: lines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }),
          '@media print': {
            display: 'block',
            WebkitLineClamp: 'unset',
            overflow: 'visible',
          },
        }}
      >
        {children}
      </Box>
      {isTruncatable && (
        <Flex justifyContent={toggleAlignment}>
          <AbstractButton
            aria-expanded={expanded}
            aria-controls={contentId}
            onClick={() => onExpandChange(!expanded)}
            color={componentTokens.buttons.colors.link.primary.text.resting}
            _hover={{ color: componentTokens.buttons.colors.link.primary.text.hover }}
          >
            <Text variant="body/sm" color="inherit">
              {expanded ? 'Show less' : 'Show more'}
            </Text>
          </AbstractButton>
        </Flex>
      )}
    </Flex>
  );
};

export default CollapsibleTextV3;
